<template>
  <div class="share-menu">
    <div class="share-menu__title">
      <Speaker class="share-menu__icon" />
      <span class="share-menu__title-text">
        Tell your friends
      </span>
      <CaretDown class="share-menu__icon share-menu__icon--hoverable share-menu__icon--lowered" />
    </div>
    <div class="share-menu__content">
      <p>Tell your friends about the new way to create professional visuals for their beats.</p>
      <big-button
        class="share-menu__copy-btn"
        text="Copy link to share"
        type="secondary"
        :is-disabled="false"
        :is-loading="false"
        @click="copyLink"
      >
        <CopyIcon />
      </big-button>
      <ul class="share-menu__socials-list">
        <li @click="openShareDialog('facebook')">
          <FacebookIcon class="share-menu__social-icon" />
        </li>
        <li @click="openShareDialog('twitter')">
          <TwitterIcon class="share-menu__social-icon" />
        </li>
        <li @click="emailShare">
          <EmailIcon class="share-menu__social-icon" />
        </li>
      </ul>
    </div>
  </div>
</template>
<script setup lang="ts">
import Speaker from '@/assets/Speaker.svg'
import CaretDown from '@/assets/CaretDown.svg'
import BigButton from '@/components/common/BigButton.vue'
import CopyIcon from '@/assets/CopyIcon.svg'
import FacebookIcon from '@/assets/FacebookIcon.svg'
import TwitterIcon from '@/assets/TwitterIcon.svg'
import EmailIcon from '@/assets/EmailIcon.svg'
import { useApplicationStore } from '@/store/application-store'

const applicationStore = useApplicationStore()

const getVisualizerURL = () => {
  return 'https://serato.com/visualizer'
}

const getFacebookShareURL = () => {
  return `https://www.facebook.com/sharer.php?u=${getVisualizerURL()}`
}

const getTwitterShareURL = () => {
  const body = encodeURIComponent(
    'Get early access to Serato Visualizer and create custom-built, attention-grabbing videos for your music.'
  )

  return `https://twitter.com/intent/tweet?url=${getVisualizerURL()}&text=${body}`
}

const copyLink = () => {
  navigator.clipboard.writeText(getVisualizerURL()).then(
    () => {
      applicationStore.pushSnackbarMessage({
        state: 'success',
        header: 'Link copied to clipboard'
      })
    }
  )
}

const openShareDialog = (targetId: string) => {
  const url = targetId === 'facebook' ? getFacebookShareURL() : getTwitterShareURL()
  window.open(url, `${targetId}-share-dialog`, 'width=626,height=436')
}

const emailShare = () => {
  const subject = encodeURIComponent('Your invite to get your music seen with Serato Visualizer')
  const body = encodeURIComponent(`Wanting to build your artistic brand and get more heat on your beats? Serato Visualizer is designed for music producers and allows you to stand out from the crowd with custom-built videos optimized for social media. 

Grab the attention of your friends and fans with professional-level videos made from Serato’s extensive content library or with over 3 million videos, photos, and stickers from Pexels. 

Serato Visualizer is free to use with no limits or watermarks, head to ${getVisualizerURL()} and get your music seen today.`
  )
  window.open(`mailto:?subject=${subject}&body=${body}`)
}
</script>
<style lang="scss">
.share-menu {
  display: block;
  position: relative;

  &__title {
    display: flex;
    align-items: center;
    height: 40px;
    cursor: pointer;
    gap: 8px;
  }

  &__icon {
    fill: $link-blue;
    transition: all 200ms $animationCurve;

    &--lowered {
      position: relative;
      top: 1px;
    }
  }

  &__title-text {
    font-size: 14px;
    font-family: $font-medium;
    color: $link-blue;
  }

  &__content {
    left: -20px;
    visibility: hidden;
    opacity: 0;
    background-color: $grey-85;
    border-radius: 4px;
    margin-top: 12px;
    min-width: 240px;
    transition: all 200ms $animationCurve;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    padding: 24px 24px 16px;
    position: absolute;
    list-style-type: none;
    z-index: 1;

    > p {
      font-size: 14px;
      line-height: 20px;
      font-family: $font-regular;
      color: $white;
    }
  }

  &__copy-btn {
    padding: 10px 15px;
    font-size: 14px;
    font-family: $font-medium;
    line-height: 17px;
    background-color: $purple;
    transition: all 200ms $animationCurve;

    &:hover {
      background-color: $purple-light;
    }

    svg {
      fill: $white;
    }
  }

  &__socials-list {
    list-style: none;
    display: flex;
    gap: 20px;
  }

  &__social-icon {
    cursor: pointer;
    fill: $white;
    height: 20px;
  }

  &:hover {
    .share-menu__content {
      opacity: 1;
      visibility: visible;
    }

    .share-menu__icon--hoverable {
      fill: $white;
    }
  }
}
</style>
  